import { useState } from 'react';
import useSWR from 'swr';
import Card from 'components/card';
import {
  ReadPermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { orderQueueService } from 'views/services/orderQueueService';
import { IQueueRow } from 'views/types/orderQueue';
import { formatTokenAmount } from 'utils/amountUtils';

const PAGE_SIZE = 20;

const headerClass =
  'border-b border-gray-200 pb-2 pr-3 text-start text-xs font-bold uppercase text-gray-600 dark:border-white/20 dark:text-white';
const cellClass = 'py-2 pr-3 text-sm text-navy-700 dark:text-white';

const shortId = (id: string) => `${id.slice(0, 8)}…`;

export const OrderQueue = () => {
  const [type, setType] = useState<'' | 'SWAP' | 'WITHDRAW'>('');
  const [onlyLocked, setOnlyLocked] = useState(false);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading, mutate } = useSWR(
    ['/api/admin/order-queue', type, onlyLocked, onlyErrors, search, page],
    () =>
      orderQueueService.getOrderQueue({
        type: type || undefined,
        page,
        pageSize: PAGE_SIZE,
        onlyLocked,
        onlyErrors,
        search: search || undefined,
      }),
    { refreshInterval: 5000 }
  );

  const rows: IQueueRow[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const runAction = async (
    action: 'retry' | 'forceExternal' | 'unlock',
    id: string
  ) => {
    try {
      setBusyId(id);
      if (action === 'retry') await orderQueueService.retry(id);
      else if (action === 'forceExternal')
        await orderQueueService.forceExternal(id);
      else await orderQueueService.unlock(id);
      await mutate();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ReadPermissionGate
      resource={PERMISSION_RESOURCES.ORDER_QUEUE}
      fallback={
        <div className="mt-5 text-center text-gray-600 dark:text-white">
          You don't have access to the Order Queue page.
        </div>
      }
    >
      <div className="mt-3">
        <Card extra="p-4">
          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <select
              className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
              value={type}
              onChange={e => {
                setType(e.target.value as '' | 'SWAP' | 'WITHDRAW');
                setPage(0);
              }}
            >
              <option value="">All types</option>
              <option value="SWAP">Swap</option>
              <option value="WITHDRAW">Withdraw</option>
            </select>
            <input
              className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
              placeholder="Search id / client id"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-white">
              <input
                type="checkbox"
                checked={onlyLocked}
                onChange={e => {
                  setOnlyLocked(e.target.checked);
                  setPage(0);
                }}
              />
              Locked only
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-white">
              <input
                type="checkbox"
                checked={onlyErrors}
                onChange={e => {
                  setOnlyErrors(e.target.checked);
                  setPage(0);
                }}
              />
              Errors only
            </label>
            {isLoading && (
              <span className="text-sm text-gray-500">Loading…</span>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={headerClass}>Order ID</th>
                  <th className={headerClass}>Client</th>
                  <th className={headerClass}>Type</th>
                  <th className={headerClass}>Asset</th>
                  <th className={headerClass}>Amount In</th>
                  <th className={headerClass}>Status</th>
                  <th className={headerClass}>Queue</th>
                  <th className={headerClass}>Locked by</th>
                  <th className={headerClass}>Retries</th>
                  <th className={headerClass}>Last error</th>
                  <th className={headerClass}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={`${row.type}-${row.id}`}
                    className={
                      row.lastError ? 'bg-red-50 dark:bg-red-900/20' : ''
                    }
                  >
                    <td className={`${cellClass} font-mono`} title={row.id}>
                      {shortId(row.id)}
                    </td>
                    <td className={cellClass}>{row.clientId ?? '—'}</td>
                    <td className={cellClass}>{row.type}</td>
                    <td className={cellClass}>{row.asset}</td>
                    <td className={cellClass}>
                      {formatTokenAmount(row.amountIn, row.decimalsIn)}
                    </td>
                    <td className={cellClass}>{row.status}</td>
                    <td className={cellClass}>
                      {row.queueState ?? '—'}
                      {row.cycleId ? (
                        <span
                          className="ml-1 text-xs text-gray-400"
                          title={row.cycleId}
                        >
                          ({shortId(row.cycleId)})
                        </span>
                      ) : null}
                    </td>
                    <td className={cellClass}>{row.lockedBy ?? '—'}</td>
                    <td className={cellClass}>{row.retryCount}</td>
                    <td
                      className={`${cellClass} max-w-[220px] truncate text-red-500`}
                      title={row.lastError ?? ''}
                    >
                      {row.lastError ?? '—'}
                    </td>
                    <td className={cellClass}>
                      <UpdatePermissionGate
                        resource={PERMISSION_RESOURCES.ORDER_QUEUE}
                      >
                        <div className="flex gap-2">
                          <button
                            disabled={busyId === row.id}
                            onClick={() => runAction('retry', row.id)}
                            className="rounded bg-brand-500 px-2 py-1 text-xs text-white disabled:opacity-50"
                          >
                            Retry
                          </button>
                          <button
                            disabled={busyId === row.id}
                            onClick={() => runAction('forceExternal', row.id)}
                            className="rounded bg-amber-500 px-2 py-1 text-xs text-white disabled:opacity-50"
                          >
                            External
                          </button>
                          <button
                            disabled={busyId === row.id}
                            onClick={() => runAction('unlock', row.id)}
                            className="rounded bg-gray-500 px-2 py-1 text-xs text-white disabled:opacity-50"
                          >
                            Unlock
                          </button>
                        </div>
                      </UpdatePermissionGate>
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td
                      className="py-4 text-center text-sm text-gray-500"
                      colSpan={11}
                    >
                      No in-flight orders.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-white">
              {total} order(s) · page {page + 1} / {pageCount}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
              >
                Prev
              </button>
              <button
                disabled={page + 1 >= pageCount}
                onClick={() => setPage(p => p + 1)}
                className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
              >
                Next
              </button>
            </div>
          </div>
        </Card>
      </div>
    </ReadPermissionGate>
  );
};

export default OrderQueue;
